import { useCallback, useMemo, useRef, useState } from "react";
import { Col, Form, Row, Tooltip, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  LoadingOutlined,
  LockOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";
import CommonButton from "./CommonButton";
import ConfirmModal from "./ConfirmModal";
import axios from "axios";
import useGet from "../hooks/useGet";
import { getDealerNo } from "../hooks/getUserRole";
import CustomSelect from "./inputWidgets/CustomSelect";
import { booleanOptions } from "../../utils/Common";
import IntuitCardFields from "./IntuitCardFields";
import {
  brandKeyFromCard,
  CardBrandIcon,
  formatCardBrandLabel,
  formatExpiresOn,
  getCardExpirationError,
  MAX_SAVED_CARDS,
  normalizeCardsResponse,
  parseCardExpiration,
} from "../utils/cardPaymentUtils";
import { formatIntuitError, tokenizeIntuitCard } from "../utils/intuitPayments";

const { Text } = Typography;

const SKELETON_SHIMMER = `
@keyframes skeleton-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`;

const skeletonBar = {
  backgroundColor: "#EEEEEE",
  backgroundImage:
    "linear-gradient(to right, transparent 0%, #FDFDFD 50%, transparent 100%)",
  backgroundSize: "200% 100%",
  animation: "skeleton-shimmer 2.5s infinite linear",
};

function CardListSkeleton({ rows = 2 }) {
  return (
    <>
      <style>{SKELETON_SHIMMER}</style>
      <div className="flex flex-col gap-2.5" aria-busy="true" aria-label="Loading payment methods">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="rounded-md border border-[#e5e7eb] bg-white px-3 py-2.5 flex items-center gap-3"
          >
            <span
              className="inline-flex h-[32px] w-[48px] shrink-0 rounded-[3px] border border-[#e4e7ec]"
              style={skeletonBar}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="block h-[13px] w-[180px] max-w-full rounded-[3px]"
                  style={skeletonBar}
                />
              </div>
              <span
                className="block h-[11px] w-[140px] max-w-[70%] rounded-[3px] mt-1.5"
                style={skeletonBar}
              />
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span
                className="block h-[20px] w-[20px] rounded-[4px]"
                style={skeletonBar}
              />
              <span
                className="block h-[20px] w-[20px] rounded-[4px]"
                style={skeletonBar}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function getApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

const apiBase = getApiBase();

function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return apiBase ? `${apiBase}${p}` : p;
}

/**
 * Dealer Payment Method CRUD (Intuit QuickBooks Payments for add-card).
 * List/Add:  /api/dealers/{dealerNo}/cards/
 * Set default / Delete:  /api/dealers/{dealerNo}/cards/{cardNo}/
 */
export const ManageCardsModal = ({
  setIsModalOpen,
  dealerNo: dealerNoProp,
  requiredMessage,
}) => {
  const [form] = Form.useForm();
  /** list | add */
  const [view, setView] = useState("list");
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [defaultingKey, setDefaultingKey] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const cvvInputRef = useRef(null);

  const sessionDealerNo = getDealerNo();
  const dealerNo =
    dealerNoProp != null && dealerNoProp !== ""
      ? dealerNoProp
      : sessionDealerNo;
  const dealerNoStr =
    dealerNo != null && dealerNo !== "" ? String(dealerNo) : "";

  const cardsCollectionUrl = useMemo(() => {
    if (!dealerNoStr) return null;
    return apiUrl(`/api/dealers/${dealerNoStr}/cards/`);
  }, [dealerNoStr]);

  const { data, loading, error, refetch } = useGet(cardsCollectionUrl);

  const cards = useMemo(() => normalizeCardsResponse(data), [data]);

  const authHeaders = useCallback(() => {
    const token = JSON.parse(sessionStorage.getItem("userData"));
    return { Authorization: `Bearer ${token?.access_token}` };
  }, []);

  const resetForm = useCallback(() => {
    form.resetFields();
    setSaving(false);
  }, [form]);

  const handleBackToList = () => {
    setView("list");
    resetForm();
  };

  const openAdd = () => {
    if (cards.length >= MAX_SAVED_CARDS) {
      openErrorNotification(
        `You can save up to ${MAX_SAVED_CARDS} cards. Remove one to add another.`,
      );
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      IsDefault: cards.length === 0 ? 1 : 0,
    });
    setView("add");
  };

  const handleSaveCard = async () => {
    if (!dealerNoStr || !cardsCollectionUrl) {
      openErrorNotification("Dealer account not found.");
      return;
    }

    try {
      const values = await form.validateFields([
        "IsDefault",
        "CardNumber",
        "ExpDate",
        "Cvc",
        "CardHolderName",
      ]);

      const parsedExp = parseCardExpiration(values.ExpDate);
      if (!parsedExp) {
        form.setFields([
          {
            name: "ExpDate",
            errors: ["Enter expiration as MM/YY."],
          },
        ]);
        return;
      }

      const expError = getCardExpirationError(parsedExp.month, parsedExp.year);
      if (expError) {
        form.setFields([{ name: "ExpDate", errors: [expError] }]);
        openErrorNotification(expError);
        return;
      }

      setSaving(true);

      const cardNumber = String(values.CardNumber || "").replace(/\D/g, "");
      const cvc = String(values.Cvc || "").replace(/\D/g, "");
      const name = String(values.CardHolderName || "").trim();
      const postalCode = String(form.getFieldValue("PostalCode") || "").trim();
      const { month: expMonth, year: expYear } = parsedExp;

      const token = await tokenizeIntuitCard({
        number: cardNumber,
        expMonth,
        expYear,
        cvc,
        name,
        postalCode: postalCode || undefined,
        isDefault: Boolean(Number(values.IsDefault ?? 0)),
      });

      await axios.post(
        cardsCollectionUrl,
        {
          PaymentSourceID: token,
        },
        { headers: authHeaders() },
      );

      openConfirmNotification("Success");
      resetForm();
      setView("list");
      refetch?.();
    } catch (err) {
      if (err?.errorFields) {
        // antd form validation
        return;
      }
      openErrorNotification(
        formatIntuitError(err?.response?.data, "") ||
          err?.response?.data?.message ||
          err?.message ||
          "Could not save card.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (card) => {
    const cardNo = card?.PaymentSourceID || card?.DealerCardNo;
    if (!dealerNoStr || cardNo == null) {
      openErrorNotification("Could not update default card.");
      return;
    }
    if (card.IsDefault) return;

    const url = apiUrl(`/api/dealers/${dealerNoStr}/cards/${cardNo}/`);
    setDefaultingKey(String(cardNo));
    try {
      await axios.put(url, { default: true }, { headers: authHeaders() });
      openConfirmNotification("Success");
      refetch?.();
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message || "Could not update default card.",
      );
    } finally {
      setDefaultingKey(null);
    }
  };

  const openDeleteConfirm = (card) => {
    if (!dealerNoStr || card?.DealerCardNo == null) {
      openErrorNotification("Could not remove card.");
      return;
    }
    setCardToDelete(card);
    setShowDeleteModal(true);
  };

  const handleDeleteCard = async () => {
    const card = cardToDelete;
    if (!dealerNoStr || card?.DealerCardNo == null) {
      openErrorNotification("Could not remove card.");
      setCardToDelete(null);
      return;
    }
    const cardNo = card.PaymentSourceID || card.DealerCardNo;
    const url = apiUrl(`/api/dealers/${dealerNoStr}/cards/${cardNo}/`);
    setDeletingKey(String(cardNo));
    try {
      await axios.delete(url, { headers: authHeaders() });
      openConfirmNotification("Success");
      refetch?.();
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message || "Could not remove card.",
      );
    } finally {
      setDeletingKey(null);
      setCardToDelete(null);
    }
  };

  const shellHeader = (title) => (
    <div className="bg-gray-200 py-[8px] px-[3px] rounded-sm flex items-center mb-2 p-2">
      <CreditCardOutlined className="ml-2 text-[16px]" />
      <h2 className="text-[14px] font-[Poppins] text-black ml-2">{title}</h2>
    </div>
  );

  const busy = deletingKey != null || defaultingKey != null;

  if (!dealerNoStr) {
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-4 px-2">
          <Text type="danger">
            No dealer account is associated with this login. Payment method is
            only available for dealer accounts.
          </Text>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: () => setIsModalOpen(false),
              },
            ]}
          />
        </div>
      </div>
    );
  }

  if (view === "list") {
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-3 px-2">
          {error ? (
            <Text type="danger" className="pl-0.5 text-sm">
              {error?.response?.data?.message ||
                "Could not load payment methods."}
            </Text>
          ) : loading ? (
            <CardListSkeleton rows={2} />
          ) : cards.length === 0 ? (
            <p
              className={`text-sm font-[Poppins] pl-0.5 m-0 ${
                requiredMessage ? "text-[#cf1322]" : "text-black"
              }`}
            >
              {requiredMessage || "No payment method added yet."}
            </p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {cards.map((card, idx) => {
                const key = card.DealerCardNo ?? idx;
                const expiresOn = formatExpiresOn(card);
                const cardKey = String(
                  card.PaymentSourceID || card.DealerCardNo || idx,
                );
                const deleting =
                  deletingKey != null && String(deletingKey) === cardKey;
                const defaulting =
                  defaultingKey != null && String(defaultingKey) === cardKey;
                const brandLabel = formatCardBrandLabel(card.CardBrand);
                const masked =
                  card.CardNumberMasked ||
                  (card.CardLast4
                    ? `xxxxxxxxxxxx${card.CardLast4}`
                    : "");
                const titleParts = [
                  brandLabel && brandLabel !== "Card" ? brandLabel : null,
                  masked || null,
                ].filter(Boolean);
                const title =
                  titleParts.length > 0 ? titleParts.join(" ") : "Saved card";
                const nameLine = String(card.CardHolderName || "").trim();
                const metaLine = [
                  nameLine || null,
                  expiresOn || null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div
                    key={String(key)}
                    className={`rounded-md border px-3 py-2.5 flex items-center gap-3 transition-colors ${
                      card.IsDefault
                        ? "border-[var(--text-color,#1677ff)]/30 bg-[var(--text-color-op-10,#e6f4ff)]"
                        : "border-[#e5e7eb] bg-white hover:border-[#cbd5e1]"
                    }`}
                  >
                    <CardBrandIcon brandKey={brandKeyFromCard(card)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-medium font-[Poppins] m-0 text-black leading-snug">
                          {title}
                        </p>
                        {card.IsDefault ? (
                          <span className="inline-flex items-center gap-1 rounded-sm px-2 py-[3px] text-[11px] font-medium font-[Poppins] leading-none text-white bg-[var(--text-color,#1677ff)]">
                            <StarFilled className="text-[10px]" />
                            Default card
                          </span>
                        ) : null}
                      </div>
                      <p className="text-[11px] text-[#64748b] m-0 mt-1 font-[Poppins] leading-snug">
                        {metaLine || "Saved payment method"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Tooltip
                        title={
                          card.IsDefault ? "Default card" : "Set as default"
                        }
                      >
                        <button
                          type="button"
                          disabled={busy || !!card.IsDefault}
                          onClick={() => handleSetDefault(card)}
                          className={`px-[6px] py-[6px] rounded-[4px] bg-transparent border-0 ${
                            card.IsDefault
                              ? "text-[var(--text-color,#1677ff)] cursor-not-allowed opacity-70"
                              : "cursor-pointer hover:bg-white/80 text-[#50565e] disabled:opacity-40 disabled:cursor-not-allowed"
                          }`}
                          aria-label={
                            card.IsDefault ? "Default card" : "Set as default"
                          }
                        >
                          {defaulting ? (
                            <LoadingOutlined />
                          ) : card.IsDefault ? (
                            <StarFilled />
                          ) : (
                            <StarOutlined />
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => openDeleteConfirm(card)}
                          className="cursor-pointer hover:bg-white/80 px-[6px] py-[6px] rounded-[4px] text-[#50565e] disabled:opacity-40 disabled:cursor-not-allowed bg-transparent border-0"
                          aria-label="Delete"
                        >
                          {deleting ? <LoadingOutlined /> : <DeleteOutlined />}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Add card",
                icon: <PlusOutlined />,
                onClick: openAdd,
              },
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: () => setIsModalOpen(false),
              },
            ]}
          />
        </div>
        <ConfirmModal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          onDeleteConfirm={handleDeleteCard}
          setSelectedRowKeys={() => setCardToDelete(null)}
          content="Do you want to delete this payment method?"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[4px] overflow-hidden">
      {shellHeader("Add card")}
      <Form
        validateTrigger="onChange"
        form={form}
        layout="vertical"
        autoComplete="off"
        scrollToFirstError
      >
        <div className="mt-4 px-2">
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <CustomSelect
                label="Set as default"
                name="IsDefault"
                options={booleanOptions}
                placeholder="Please select"
                allowClear={false}
              />
            </Col>
          </Row>
          <IntuitCardFields form={form} cvvInputRef={cvvInputRef} />
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <div className="flex items-center gap-2 text-gray-500 text-sm pl-0.5">
                <LockOutlined />
                <span>Your card is securely processed.</span>
              </div>
            </Col>
          </Row>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Apply",
                icon: <CheckCircleOutlined />,
                onClick: handleSaveCard,
                loading: saving,
              },
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: handleBackToList,
              },
            ]}
          />
        </div>
      </Form>
    </div>
  );
};
